import { useState, useEffect, useMemo, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { T, amountStyle } from '../theme';
import { formatAmount, formatDate } from '../utils/formatters';
import { BIZ_CLASS } from '../utils/constants';
import { Sparkles, Lock, Link2, X, Check, RefreshCw } from 'lucide-react';

const reconcileFn = httpsCallable(functions, 'reconcileTransactions', { timeout: 60000 });

const REPORT_ICONS = { credit_card: '💳', bank: '🏦', expense: '🧾' };
const CLASS_LABELS = Object.fromEntries(Object.entries(BIZ_CLASS).map(([k, v]) => [k, v.label]));
const CLASS_COLORS = Object.fromEntries(Object.entries(BIZ_CLASS).map(([k, v]) => [k, { color: v.color, bg: v.bg }]));
const UNCLASSIFIED = new Set(['unclassified', 'unclassified_credit', 'unclassified_debit']);

// ─── Heuristic scorer ─────────────────────────────────────────────────────────

function heuristicScore(exp, tx) {
  const expAmt = parseFloat(exp.convertedTotal ?? exp.total ?? 0);
  const txAmt  = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
  if (!expAmt || !txAmt) return 0;

  const pct = Math.abs(expAmt - txAmt) / Math.max(expAmt, txAmt);
  if (pct > 0.15) return 0;

  let score = 0;
  if      (pct <= 0.02) score += 5;
  else if (pct <= 0.08) score += 2;
  else                  score += 1;

  if (exp.date && tx.date) {
    const days = Math.abs(new Date(exp.date) - new Date(tx.date)) / 86400000;
    if (days > 8) return 0;
    if      (days <= 1) score += 4;
    else if (days <= 3) score += 2;
    else if (days <= 4) score += 1;
  }

  const vA = (exp.vendor || '').trim().toLowerCase();
  const vB = (tx.vendor  || '').trim().toLowerCase();
  if (vA && vB) {
    if (vA === vB) score += 4;
    else if (vA.includes(vB) || vB.includes(vA)) score += 2;
    else if (vA.split(/\s+/).filter(w => w.length > 3).some(w => vB.includes(w))) score += 1;
  }

  const catA = exp.category?.trim().toLowerCase();
  const catB = tx.category?.trim().toLowerCase();
  if (catA && catB) {
    if (catA === catB) score += 2;
    else score -= 2;
  }

  return score;
}

const MIN_CONFIDENCE = 0.65;

// Greedy best-first matching: assigns each item to at most one partner
function buildHeuristicMatches(expenses, transactions) {
  const pairs = [];
  for (const tx of transactions) {
    for (const exp of expenses) {
      const score = heuristicScore(exp, tx);
      const confidence = Math.min(0.95, score / 12);
      if (confidence >= MIN_CONFIDENCE) pairs.push({ exp, tx, score, confidence });
    }
  }
  pairs.sort((a, b) => b.score - a.score);

  const usedExp = new Set();
  const usedTx  = new Set();
  const result  = [];
  for (const p of pairs) {
    if (usedExp.has(p.exp.id) || usedTx.has(p.tx.id)) continue;
    usedExp.add(p.exp.id);
    usedTx.add(p.tx.id);
    result.push(p);
  }
  return result;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ImageViewerModal({ src, onClose }) {
  const [pos, setPos] = useState({ x: Math.max(20, window.innerWidth / 2 - 160), y: 80 });
  const [dragging, setDragging] = useState(false);
  const [dragOrigin, setDragOrigin] = useState(null);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const cx = e.touches?.[0]?.clientX ?? e.clientX;
      const cy = e.touches?.[0]?.clientY ?? e.clientY;
      setPos({ x: dragOrigin.px + (cx - dragOrigin.mx), y: dragOrigin.py + (cy - dragOrigin.my) });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, dragOrigin]);

  const startDrag = (e) => {
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    setDragging(true);
    setDragOrigin({ mx: cx, my: cy, px: pos.x, py: pos.y });
    e.preventDefault();
  };

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 800,
      background: '#fff', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
      resize: 'both', overflow: 'auto', minWidth: 200, minHeight: 180, width: 320, height: 420,
      display: 'flex', flexDirection: 'column',
    }}>
      <div
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 10px', background: '#F3F4F6', borderRadius: '12px 12px 0 0',
          cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none', flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Receipt</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
          <X size={14} color="#6B7280" />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
        <img src={src} alt="Receipt" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} />
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
      <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, flexShrink: 0, minWidth: 52, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</span>
      <span style={{ fontSize: 11, color: '#374151', ...(mono ? amountStyle() : {}) }}>{value}</span>
    </div>
  );
}

function TxCard({ tx, normalizedVendor, side }) {
  const [showImage, setShowImage] = useState(false);
  const isExpense = tx._reportType === 'expense';
  const isCredit  = tx.transactionDirection === 'credit';
  const icon      = REPORT_ICONS[tx._reportType] ?? '🧾';
  const bg        = side === 'left' ? '#F0FDF4' : '#EFF6FF';

  const rawAmt  = parseFloat(tx.total ?? tx.amount ?? 0);
  const convAmt = parseFloat(tx.convertedTotal ?? 0);
  const showConv = tx.convertedCurrency && tx.convertedCurrency !== tx.currency && convAmt > 0;

  const cls = tx.businessClassification;
  const clsInfo = cls && !UNCLASSIFIED.has(cls) ? CLASS_COLORS[cls] : null;

  return (
    <div style={{ flex: 1, minWidth: 0, padding: '10px 12px', background: bg, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Vendor */}
      <div style={{ fontSize: 13, fontWeight: 800, color: '#111', wordBreak: 'break-word' }}>
        {tx.vendor || 'Unknown'}
      </div>
      {normalizedVendor && normalizedVendor !== tx.vendor && (
        <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 700 }}>✨ {normalizedVendor}</div>
      )}

      {/* Primary amount */}
      <div style={{ fontSize: 14, fontWeight: 700, color: isCredit ? '#15803d' : '#111', marginTop: 2, ...amountStyle() }}>
        {isCredit ? '+' : ''}{formatAmount(rawAmt, tx.currency)}
      </div>

      {/* Converted amount */}
      {showConv && (
        <Row label="Converted" value={`${formatAmount(convAmt, tx.convertedCurrency)}${tx.conversionRate ? ` @ ${Number(tx.conversionRate).toFixed(4)}` : ''}`} mono />
      )}

      {/* Subtotal / Tax / Tip (expense only) */}
      {isExpense && parseFloat(tx.subtotal) > 0 && <Row label="Subtotal" value={formatAmount(tx.subtotal, tx.currency)} mono />}
      {isExpense && parseFloat(tx.tax)      > 0 && <Row label="Tax"      value={formatAmount(tx.tax,      tx.currency)} mono />}
      {isExpense && parseFloat(tx.tip)      > 0 && <Row label="Tip"      value={formatAmount(tx.tip,      tx.currency)} mono />}

      <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', margin: '3px 0' }} />

      {/* Date */}
      <Row label="Date" value={formatDate(tx.date)} />
      {tx.start_date && tx.end_date && tx.start_date !== tx.date && (
        <Row label="Period" value={`${formatDate(tx.start_date)} – ${formatDate(tx.end_date)}`} />
      )}

      {/* Report */}
      <Row label="Report" value={`${icon} ${tx._reportName}`} />

      {/* Category */}
      {tx.category && <Row label="Category" value={tx.category} />}

      {/* Transaction type badge */}
      {tx.transactionType && (
        <div style={{ marginTop: 2 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
            background: isCredit ? '#DCFCE7' : '#FEF3C7',
            color: isCredit ? '#15803d' : '#92400e',
            textTransform: 'capitalize',
          }}>
            {tx.transactionType}
          </span>
        </div>
      )}

      {/* Location */}
      {tx.location && <Row label="Location" value={`📍 ${tx.location}`} />}

      {/* Meal details (expense only) */}
      {tx.mealType      && <Row label="Meal"  value={tx.mealType} />}
      {tx.guestName     && <Row label="Guest" value={`${tx.guestName}${tx.guestAffiliation ? ` (${tx.guestAffiliation})` : ''}`} />}

      {/* Notes */}
      {tx.notes && (
        <Row label="Notes" value={`"${tx.notes}"`} />
      )}

      {/* Business classification */}
      {clsInfo && (
        <div style={{ marginTop: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: clsInfo.bg, color: clsInfo.color }}>
            {CLASS_LABELS[cls]}
          </span>
        </div>
      )}

      {/* Receipt link */}
      {tx.imagePreview && (
        <>
          <button
            onClick={() => setShowImage(v => !v)}
            style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: '#374151', fontWeight: 600, marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}
          >
            📎 {showImage ? 'Hide receipt' : 'View receipt'}
          </button>
          {showImage && <ImageViewerModal src={tx.imagePreview} onClose={() => setShowImage(false)} />}
        </>
      )}
    </div>
  );
}

function MatchCard({ match, normalizedVendors, onLink, onDismiss, linked, dismissed }) {
  const key = `${match.exp.id}:${match.tx.id}`;
  const pct = Math.round(match.confidence * 100);
  const reason = match.reason;

  if (dismissed.has(key)) return null;
  // if (pct < 50) return null;

  if (linked.has(key)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#F0FDF4', borderRadius: 12, border: '1.5px solid #86efac', marginBottom: 8 }}>
        <Check size={16} color="#16a34a" />
        <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
          Linked: {match.exp.vendor} ↔ {match.tx.vendor}
        </span>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 8, padding: '10px 10px 6px' }}>
        <TxCard tx={match.exp} normalizedVendor={normalizedVendors[match.exp.id]} side="left" />
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Link2 size={16} color="#9CA3AF" />
        </div>
        <TxCard tx={match.tx} normalizedVendor={normalizedVendors[match.tx.id]} side="right" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 8px', borderTop: '1px solid #F3F4F6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 60, height: 5, borderRadius: 3, background: '#E5E7EB', overflow: 'hidden',
          }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#9CA3AF', borderRadius: 3 }} />
          </div>
          <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>{pct}%</span>
          {reason && <span style={{ fontSize: 11, color: '#9CA3AF' }}>· {reason}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => onDismiss(key)}
            style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <X size={12} /> Skip
          </button>
          <button
            onClick={() => onLink(match.exp, match.tx, key)}
            style={{ padding: '4px 12px', borderRadius: 7, border: 'none', background: T.tealDeep, cursor: 'pointer', fontSize: 12, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <Link2 size={12} /> Link
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ReconcileModal({ allLoaded, savedFiles, uid, defaultCurrency, canAiReconcile, onLink, onClassify, onClose }) {

  const [aiResult,  setAiResult]  = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState(null);
  const [dismissed,       setDismissed]       = useState(new Set());
  const [linked,          setLinked]          = useState(new Set());
  const [linking,         setLinking]         = useState(null);
  const [sessionExpIds,   setSessionExpIds]   = useState(new Set());
  const [sessionTxIds,    setSessionTxIds]    = useState(new Set());

  // Derive unlinked expenses and unlinked CC/bank transactions
  const { unlinkedExpenses, unlinkedTransactions } = useMemo(() => {
    const expenses = [];
    const transactions = [];
    for (const [reportName, data] of Object.entries(allLoaded)) {
      const fi = savedFiles.find(f => f.name === reportName);
      const rType = fi?.reportType ?? 'expense';
      for (const tx of (data ?? [])) {
        if (tx.pairedWithId) continue;
        if (rType === 'expense' ? sessionExpIds.has(tx.id) : sessionTxIds.has(tx.id)) continue;
        const tagged = { ...tx, _reportName: reportName, _reportType: rType };
        if (rType === 'expense') expenses.push(tagged);
        else transactions.push(tagged);
      }
    }
    return { unlinkedExpenses: expenses, unlinkedTransactions: transactions };
  }, [allLoaded, savedFiles, sessionExpIds, sessionTxIds]);

  // Heuristic matches (always available)
  const heuristicMatches = useMemo(
    () => buildHeuristicMatches(unlinkedExpenses, unlinkedTransactions),
    [unlinkedExpenses, unlinkedTransactions]
  );

  // Merge AI matches with heuristic matches; AI takes precedence for shared items
  const displayMatches = useMemo(() => {
    if (!aiResult?.suggestedMatches?.length) return heuristicMatches;

    const expMap  = Object.fromEntries(unlinkedExpenses.map(e => [e.id, e]));
    const txMap   = Object.fromEntries(unlinkedTransactions.map(t => [t.id, t]));
    const usedExp = new Set();
    const usedTx  = new Set();
    const result  = [];

    // AI matches first
    for (const m of aiResult.suggestedMatches) {
      if (m.confidence < MIN_CONFIDENCE) continue;
      const exp = expMap[m.expenseId];
      const tx  = txMap[m.txId];
      if (!exp || !tx) continue;
      if (usedExp.has(exp.id) || usedTx.has(tx.id)) continue;
      usedExp.add(exp.id);
      usedTx.add(tx.id);
      result.push({ exp, tx, confidence: m.confidence, reason: m.reason, source: 'ai' });
    }

    // Then heuristic matches for items AI didn't cover
    for (const m of heuristicMatches) {
      if (usedExp.has(m.exp.id) || usedTx.has(m.tx.id)) continue;
      usedExp.add(m.exp.id);
      usedTx.add(m.tx.id);
      result.push({ ...m, source: 'heuristic' });
    }

    return result.sort((a, b) => b.confidence - a.confidence);
  }, [aiResult, heuristicMatches, unlinkedExpenses, unlinkedTransactions]);

  // Classification suggestions for unmatched items (AI only)
  const classificationSuggestions = useMemo(() => {
    if (!aiResult?.suggestedClassifications) return [];
    const linkedExpIds = new Set(displayMatches.map(m => m.exp.id));
    const linkedTxIds  = new Set(displayMatches.map(m => m.tx.id));
    const allUnmatched = [
      ...unlinkedExpenses.filter(e => !linkedExpIds.has(e.id)),
      ...unlinkedTransactions.filter(t => !linkedTxIds.has(t.id)),
    ];
    return allUnmatched
      .map(tx => ({ tx, cls: aiResult.suggestedClassifications[tx.id] }))
      .filter(x => x.cls && x.cls !== x.tx?.businessClassification);
  }, [aiResult, displayMatches, unlinkedExpenses, unlinkedTransactions]);

  const normalizedVendors = aiResult?.normalizedVendors ?? {};

  const runAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await reconcileFn({ expenses: unlinkedExpenses, transactions: unlinkedTransactions });
      setAiResult(result.data);
    } catch (err) {
      setAiError(err.message || 'AI analysis failed — please try again.');
    } finally {
      setAiLoading(false);
    }
  }, [unlinkedExpenses, unlinkedTransactions]);

  const handleLink = useCallback(async (expTx, ccTx, key) => {
    setLinking(key);
    try {
      await onLink(expTx, ccTx);
      setLinked(prev => new Set([...prev, key]));
      setSessionExpIds(prev => new Set([...prev, expTx.id]));
      setSessionTxIds(prev => new Set([...prev, ccTx.id]));
    } finally {
      setLinking(null);
    }
  }, [onLink]);

  const handleDismiss = useCallback((key) => {
    setDismissed(prev => new Set([...prev, key]));
  }, []);

  const visibleCount = displayMatches.filter(m => !dismissed.has(`${m.exp.id}:${m.tx.id}`) && !linked.has(`${m.exp.id}:${m.tx.id}`)).length;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 600,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, fontFamily: T.fontBody,
    }}>
      <div style={{
        background: T.appBg, borderRadius: 20, width: '100%', maxWidth: 680,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#111' }}>Link Transactions</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {unlinkedExpenses.length} unlinked expense item{unlinkedExpenses.length !== 1 ? 's' : ''} · {unlinkedTransactions.length} unlinked CC/bank entr{unlinkedTransactions.length !== 1 ? 'ies' : 'y'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex' }}>
            <X size={18} color="#374151" />
          </button>
        </div>

        {/* AI toolbar */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #F3F4F6', flexShrink: 0, background: '#fff' }}>
          {canAiReconcile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={runAiAnalysis}
                disabled={aiLoading || unlinkedExpenses.length === 0 || unlinkedTransactions.length === 0}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  background: aiLoading ? '#E5E7EB' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: aiLoading ? '#9CA3AF' : '#fff',
                }}
              >
                {aiLoading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
                {aiLoading ? 'Analyzing…' : aiResult ? 'Re-analyze with AI' : 'Analyze with AI'}
              </button>
              {aiResult && !aiLoading && (
                <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                  ✓ AI analysis complete · {aiResult.suggestedMatches?.length ?? 0} match{aiResult.suggestedMatches?.length !== 1 ? 'es' : ''} found
                </span>
              )}
              {aiError && <span style={{ fontSize: 12, color: '#dc2626' }}>{aiError}</span>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#FFFBEB', borderRadius: 10, border: '1px solid #fde68a' }}>
              <Lock size={14} color="#d97706" />
              <span style={{ fontSize: 13, color: '#92400e' }}>
                <strong>AI Analysis</strong> is a Pro feature. Heuristic matching is available on all plans.
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* Match suggestions */}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Suggested matches {visibleCount > 0 ? `(${visibleCount})` : ''}
            {displayMatches.some(m => m.source === 'ai') && (
              <span style={{ marginLeft: 6, fontSize: 10, color: '#8b5cf6', fontWeight: 700, background: '#F5F3FF', borderRadius: 4, padding: '1px 5px' }}>AI</span>
            )}
          </div>

          {displayMatches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>
              {unlinkedExpenses.length === 0 || unlinkedTransactions.length === 0
                ? 'No unlinked items to reconcile — select reports that include both expense and CC/bank files.'
                : 'No matches found. Try AI analysis for better results.'}
            </div>
          ) : (
            <>
              {displayMatches.map(m => (
                <MatchCard
                  key={`${m.exp.id}:${m.tx.id}`}
                  match={m}
                  normalizedVendors={normalizedVendors}
                  onLink={(exp, tx, key) => handleLink(exp, tx, key)}
                  onDismiss={handleDismiss}
                  linked={linked}
                  dismissed={dismissed}
                  linking={linking}
                />
              ))}
              {visibleCount === 0 && displayMatches.length > 0 && (
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#9CA3AF', fontSize: 13 }}>
                  All suggestions handled.
                </div>
              )}
            </>
          )}

          {/* AI classification suggestions */}
          {classificationSuggestions.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 24, marginBottom: 10 }}>
                AI Classification Suggestions ({classificationSuggestions.length})
              </div>
              {classificationSuggestions.map(({ tx, cls }) => (
                <div key={tx.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 6 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {normalizedVendors[tx.id] || tx.vendor}
                      {normalizedVendors[tx.id] && normalizedVendors[tx.id] !== tx.vendor && (
                        <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}> ({tx.vendor})</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>
                      {REPORT_ICONS[tx._reportType] ?? '🧾'} {tx._reportName} · {formatDate(tx.date)}
                      <span style={{ marginLeft: 6, fontWeight: 700, color: '#6366f1' }}>→ {CLASS_LABELS[cls] ?? cls}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onClassify(tx, cls)}
                    style={{ flexShrink: 0, marginLeft: 10, padding: '4px 10px', borderRadius: 7, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  >
                    Accept
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
